import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import {
  yieldToMainThread,
  getViewParameterValue,
  ResizeNotifier,
} from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

export default class VariantPicker extends Component {
  #pendingRequestUrl;
  #abortController;
  #checkedIndices = [];
  #radios = [];

  #confirmedColor = null;
  #confirmedSize = null;
  #sizesRevealed = false;
  #confirmationProductId = null;
  #confirmationLoading = false;
  #confirmationFailed = false;
  #requestGeneration = 0;

  #resizeObserver = new ResizeNotifier(() => {
    this.updateVariantPickerCss();
  });

  #handleChange = (event) => {
    this.variantChanged(event);
  };

  get requiresColorConfirmation() {
    return (
      this.dataset.requiresColorConfirmation === 'true' &&
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card, quick-add-dialog')
    );
  }

  get confirmationMessage() {
    if (!this.requiresColorConfirmation) return '';

    if (!this.#confirmedColor) return 'SELECT A COLOR';

    const hasSize = Array.from(
      this.querySelectorAll('[data-option-name]')
    ).some((element) => {
      return element.dataset.optionName?.trim().toLowerCase() === 'size';
    });

    if (hasSize && !this.#confirmedSize) return 'SELECT A SIZE';

    if (this.#confirmationFailed) return 'SELECT OPTIONS AGAIN';

    return '';
  }

  get confirmationLoading() {
    return (
      this.requiresColorConfirmation &&
      this.#confirmationLoading
    );
  }

  connectedCallback() {
    super.connectedCallback();

    this.#rebuildRadioState();
    this.addEventListener('change', this.#handleChange);
    this.#resizeObserver.observe(this);

    this.#syncConfirmationUI();

    // The product-form component may connect after this picker.
    queueMicrotask(() => {
      if (this.isConnected) {
        this.#notifyConfirmationForms();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('change', this.#handleChange);
    this.#resizeObserver.disconnect();
  }

  #rebuildRadioState() {
    this.#radios = [];
    this.#checkedIndices = [];

    const fieldsets = this.refs.fieldsets || [];

    for (const fieldset of fieldsets) {
      const radios = Array.from(
        fieldset.querySelectorAll('input')
      );

      const currentIndex = radios.findIndex((radio) => {
        return radio.checked;
      });

      const previousIndex = radios.findIndex((radio) => {
        return radio.dataset.previousChecked === 'true';
      });

      this.#radios.push(radios);
const indices = [];

      if (currentIndex !== -1) {
        indices.push(currentIndex);

        if (
          previousIndex !== -1 &&
          previousIndex !== currentIndex
        ) {
          indices.push(previousIndex);
        }
      }

      this.#checkedIndices.push(indices);
    }
  }

  #syncConfirmationUI() {
    if (!this.requiresColorConfirmation) return;

    if (
      this.#confirmationProductId !== this.dataset.productId
    ) {
      this.#confirmationProductId = this.dataset.productId;
      this.#confirmedColor = null;
      this.#confirmedSize = null;
      this.#sizesRevealed = false;
      this.#confirmationFailed = false;
    }

    const oneSizeFallback = this.querySelector('[data-one-size-fallback]');
    if (oneSizeFallback) {
      oneSizeFallback.hidden = !this.#sizesRevealed;
      oneSizeFallback.style.display = this.#sizesRevealed ? '' : 'none';
    }

    for (const fieldset of this.querySelectorAll('fieldset')) {
      const inputs = Array.from(
        fieldset.querySelectorAll('input')
      );

      const name = (
        fieldset.dataset.optionName ||
        inputs[0]?.dataset.optionName ||
        ''
      ).trim().toLowerCase();

      const isColor = name === 'color' || name === 'colour';
      const isSize = name === 'size';

      if (isSize) {
        fieldset.hidden = !this.#sizesRevealed;
        fieldset.style.display = this.#sizesRevealed ? '' : 'none';
      }

      if (!isColor && !isSize) continue;

      let confirmedValue = isColor
        ? this.#confirmedColor
        : this.#confirmedSize;

      // Do not preserve a selection that the new product no longer has.
      if (
        confirmedValue !== null &&
        !inputs.some((input) => input.value === confirmedValue)
      ) {
        confirmedValue = null;

        if (isColor) {
          this.#confirmedColor = null;
        } else {
          this.#confirmedSize = null;
        }
      }

      for (const input of inputs) {
        const checked =
          confirmedValue !== null &&
          input.value === confirmedValue;

        input.checked = checked;
        input.dataset.currentChecked = String(checked);
        input.dataset.previousChecked = 'false';
      }

      if (isColor) {
        const valueLabel = fieldset.querySelector(
          '.variant-option__swatch-value'
        );

        if (valueLabel) {
          valueLabel.textContent = this.#confirmedColor || '';
        }
      }
    }

    this.#rebuildRadioState();
    this.updateVariantPickerCss();
    this.#notifyConfirmationForms();
  }

  #notifyConfirmationForms() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    for (
      const form of section.querySelectorAll(
        'product-form-component'
      )
    ) {
      if (form.dataset.productId === this.dataset.productId) {
        form.syncColorConfirmation?.();
      }
    }
  }
variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption) return;

    if (this.requiresColorConfirmation) {
      const name =
        selectedOption.dataset.optionName?.trim().toLowerCase();

      const value = selectedOption.value;

      if (name === 'color' || name === 'colour') {
        this.#confirmedColor = value;
        this.#sizesRevealed = true;
      } else if (name === 'size') {
        const isChangingSelectedSize =
          this.#confirmedSize !== null &&
          this.#confirmedSize !== value;

        this.#confirmedSize = value;

        if (isChangingSelectedSize) {
          this.#confirmedColor = null;
        }
      }

      this.#confirmationFailed = false;
      this.#confirmationLoading = true;

      // Remove unconfirmed defaults before requesting the new variant.
      this.#syncConfirmationUI();
    }

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;

    const loadsNewProduct =
      isOnProductPage &&
      !!newUrl &&
      newUrl !== currentUrl;

    const isOnFeaturedProductSection = Boolean(
      this.closest('featured-product-information')
    );

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const {
      optionValueId = '',
      variantId = '',
      connectedProductUrl = '',
    } = selectedOption.dataset;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      {
        morphElementSelector,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
      }
    );

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = this.refs.fieldsets || [];
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth:
        currentIndex !== undefined
          ? radios[currentIndex]?.parentElement?.offsetWidth
          : undefined,
      previousWidth:
        previousIndex !== undefined
          ? radios[previousIndex]?.parentElement?.offsetWidth
          : undefined,
    };
  }
#applyFieldsetMeasurements({
    fieldset,
    currentWidth,
    previousWidth,
  }) {
    if (currentWidth) {
      fieldset.style.setProperty(
        '--pill-width-current',
        `${currentWidth}px`
      );
    } else {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty(
        '--pill-width-previous',
        `${previousWidth}px`
      );
    } else {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements =
      this.#getFieldsetMeasurements(fieldsetIndex);

    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`
      );

      if (!targetElement) {
        throw new Error('Target element not found');
      }

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(
        target.dataset.fieldsetIndex || ''
      );

      const inputIndex = Number.parseInt(
        target.dataset.inputIndex || ''
      );

      if (
        !Number.isNaN(fieldsetIndex) &&
        !Number.isNaN(inputIndex)
      ) {
        const fieldsets = this.refs.fieldsets || [];
        const fieldset = fieldsets[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && fieldset) {
          const previousIndex =
            this.#checkedIndices[fieldsetIndex]?.[0];

          for (let index = 0; index < radios.length; index++) {
            const radio = radios[index];

            radio.checked = index === inputIndex;
            radio.dataset.currentChecked = String(
              index === inputIndex
            );

            radio.dataset.previousChecked = String(
              previousIndex !== undefined &&
              previousIndex !== inputIndex &&
              index === previousIndex
            );
          }

          this.#checkedIndices[fieldsetIndex] =
            previousIndex !== undefined &&
            previousIndex !== inputIndex
              ? [inputIndex, previousIndex]
              : [inputIndex];

          this.updateFieldsetCss(fieldsetIndex);
        }
      }

      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;

      const newSelectedOption = Array.from(
        target.options
      ).find((option) => option.value === newValue);

      if (!newSelectedOption) {
        throw new Error('Option not found');
      }

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;

    this.#pendingRequestUrl = productUrl;

const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) {
      params.push(`view=${encodeURIComponent(viewParamValue)}`);
    }

    if (this.selectedOptionsValues.length && !source) {
      params.push(
        `option_values=${this.selectedOptionsValues.join(',')}`
      );
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(
          `option_values=${sourceSelectedOptionsValues.join(',')}`
        );
      } else {
        params.push(
          `option_values=${selectedOption.dataset.optionValueId}`
        );
      }
    }

    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component':
        'section-rendering-product-card',
      'featured-product-information': this.closest(
        'featured-product-information'
      )?.id,
    };

    const closestSectionId = Object.keys(
      SECTION_ID_MAP
    ).find((sectionId) => this.closest(sectionId));

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }

      return (
        `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}` +
        `&${params.join('&')}`
      );
    }

    return `${productUrl}?${params.join('&')}`;
  }

  fetchUpdatedSection(
    requestUrl,
    { morphElementSelector, detail = {} } = {}
  ) {
    const {
      optionValueId = '',
      variantId,
      connectedProductUrl = '',
    } = detail;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const generation = ++this.#requestGeneration;

    const deferredEventPromise =
      ProductSelectEvent.createPromise();

    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );
fetch(requestUrl, {
      signal: this.#abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Variant update failed: ${response.status}`
          );
        }

        return response.text();
      })
      .then((responseText) => {
        if (generation !== this.#requestGeneration) {
          throw new DOMException(
            'A newer selection replaced this request',
            'AbortError'
          );
        }

        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );

        html.querySelector(
          'overflow-list[defer]'
        )?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(
          'variant-picker script[type="application/json"]'
        );

        const textContent =
          variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });

          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;

          const wasSwatchesExpanded =
            overflowList instanceof OverflowList &&
            overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (
            wasSwatchesExpanded &&
            overflowList instanceof OverflowList
          ) {
            overflowList.showAll();
          }
        }

        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (
            variantData &&
            typeof variantData === 'object'
          ) {
            const productViewAttr =
              variantPickerJsonScript
                ?.closest('[view-event-payload]')
                ?.getAttribute('view-event-payload')
                ?.trim();

            deferredEventPromise.resolve({
              variant:
                (
                  productViewAttr &&
                  JSON.parse(productViewAttr)
                )?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        if (
          generation === this.#requestGeneration &&
          error.name !== 'AbortError'
        ) {
          this.#confirmationFailed = true;
        }

        deferredEventPromise.reject(error);

        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        if (generation !== this.#requestGeneration) return;

        this.#pendingRequestUrl = undefined;
        this.#confirmationLoading = false;
        this.#syncConfirmationUI();
      });
  }

  updateVariantPicker(newHtml) {
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase()
    );

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId =
        newVariantPickerSource.dataset.productId;

      const newProductUrl =
        newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = {
          id: newProductId,
          url: newProductUrl,
        };
}

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        return node.dataset.key;
      },
    });

    this.#rebuildRadioState();
    this.#syncConfirmationUI();
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = this.refs.fieldsets || [];

    const measurements = fieldsets
      .map((_, index) => {
        return this.#getFieldsetMeasurements(index);
      })
      .filter((measurement) => measurement !== null);

    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(
        `No new element source found for ${elementSelector}`
      );
    }

    morph(element, newElement);

    this.#rebuildRadioState();
    this.#syncConfirmationUI();
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);

    this.#rebuildRadioState();
    this.#syncConfirmationUI();
  }

  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked'
    );

    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  getAllSelectedOptions() {
    const options = [];

    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];

      if (selected?.dataset?.optionName) {
        options.push({
          name: selected.dataset.optionName,
          value: selected.value,
        });
      }
    }

    for (
      const input of this.querySelectorAll(
        'fieldset input:checked'
      )
    ) {
if (input.dataset?.optionName) {
        options.push({
          name: input.dataset.optionName,
          value: input.value,
        });
      }
    }

    return options;
  }

  get selectedOptionId() {
    const { selectedOption } = this;

    if (!selectedOption) return undefined;

    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  get selectedOptionsValues() {
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset input:checked'
      )
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) {
        throw new Error('No option value ID found');
      }

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

